import { useEffect } from 'react';

import { useQueryClient } from '@tanstack/react-query';

import { dashboardOptions } from '@/features/dashboard/api/dashboard.options';

const CHANNEL_NAME = 'gx_dashboard_sync';

/**
 * Notifies other browser tabs that a dashboard mutation has occurred.
 * Opens a short-lived BroadcastChannel, posts the message, then closes it.
 * Other tabs refresh from the server when they receive the signal.
 */
export const broadcastDashboardMutation = (): void => {
  const channel = new BroadcastChannel(CHANNEL_NAME);
  channel.postMessage({ type: 'dashboard-cache-invalidated' });
  channel.close();
};

/**
 * Listens for dashboard mutations broadcast from other tabs and refetches
 * the dashboard query so this tab stays in sync automatically.
 *
 * Call once in AppLayout (or any persistent authenticated component).
 */
export const useDashboardSync = (): void => {
  const queryClient = useQueryClient();

  useEffect(() => {
    const channel = new BroadcastChannel(CHANNEL_NAME);

    const refetchDashboardCache = () => {
      queryClient
        .refetchQueries({ queryKey: dashboardOptions.all(), type: 'active' })
        .catch(() => undefined);
    };

    channel.onmessage = refetchDashboardCache;

    return () => {
      channel.close();
    };
  }, [queryClient]);
};
